import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/api';
import { fetchJson } from '../../../lib/apiFetchJson';
import { safeParseJson } from '@shared/parse-json';
import { DualWriteEventSchema, DualWriteEventsResponseSchema, type DualWriteEvent } from '@shared/dual-write-types';

const MAX_EVENTS = 100;

export function useDualWriteStream() {
  const { apiFetch } = useApi();
  const [events, setEvents] = useState<DualWriteEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const queryClient = useQueryClient();

  // Single shared buffer — both initial fetch and SSE write here to avoid races.
  // SSE events prepend (newest first), initial fetch appends (fills history).
  useEffect(() => {
    let cancelled = false;
    const buffer: DualWriteEvent[] = [];
    const seenKeys = new Set<string>();

    const addEvent = (evt: DualWriteEvent) => {
      const key = `${evt.timestamp}-${evt.legacyId}-${evt.type}`;
      if (seenKeys.has(key)) return;
      seenKeys.add(key);
      buffer.unshift(evt);
      if (buffer.length > MAX_EVENTS) buffer.length = MAX_EVENTS;
    };

    const flush = () => { if (!cancelled) setEvents(buffer.slice()); };

    // Fetch buffered events from server (catches events before SSE connected)
    fetchJson(apiFetch, DualWriteEventsResponseSchema, '/api/v4/admin/dual-write/events')
      .then((data) => {
        if (data.events?.length) {
          for (const evt of data.events) addEvent(evt);
          flush();
        }
      })
      .catch(() => {});

    // SSE for live updates
    const es = new EventSource('/api/v4/admin/dual-write/stream');

    es.addEventListener('ping', () => {
      if (!cancelled) setConnected(true);
    });

    es.addEventListener('dual-write', (e: MessageEvent) => {
      if (cancelled) return;
      const parsed = safeParseJson(DualWriteEventSchema, e.data);
      if (!parsed.success) return;
      addEvent(parsed.data);
      flush();
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dual-write', 'stats'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dual-write', 'rows'] });
    });

    es.onerror = () => {
      if (!cancelled) setConnected(false);
    };

    return () => {
      cancelled = true;
      es.close();
    };
  }, [apiFetch, queryClient]);

  return { events, connected };
}
